import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetFeedQuery } from 'store/services/feed/feedApi';
import type {
  FeedFilter,
  FeedTrade,
  FeedWant,
  FeedResponse,
  FeedTradeResponse,
  FeedWantResponse,
} from 'types/interfaces/feed';
import {
  CARD_SEARCH_DEBOUNCE_MS,
  FEED_PAGE_SIZE,
  FEED_WINDOW,
  POLLING_INTERVAL_MS,
  SCROLL_BUFFER_THRESHOLD_PX,
} from 'screens/Main/tabs/Feed/constants';

type FeedItem = FeedTrade | FeedWant;

interface UseFeedReturn {
  filter: FeedFilter;
  setFilter: (f: FeedFilter) => void;
  cardSearch: string;
  setCardSearch: (s: string) => void;
  items: FeedItem[];
  isLoading: boolean;
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isPaused: boolean;
  togglePause: () => void;
  pendingCount: number;
  flushPending: () => void;
  onScroll: (e: React.UIEvent<HTMLElement>) => void;
}

const isWantResponse = (r: FeedResponse, f: FeedFilter): r is FeedWantResponse =>
  f === 'new_wants';

export const useFeed = (): UseFeedReturn => {
  const [filter, setFilterState] = useState<FeedFilter>('all');
  const [cardInput, setCardInput] = useState('');
  const [cardDebounced, setCardDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [displayedItems, setDisplayedItems] = useState<FeedItem[]>([]);
  const [pendingItems, setPendingItems] = useState<FeedItem[]>([]);
  const [isPollingEnabled, setIsPollingEnabled] = useState(
    typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );

  const scrolledBeyondThresholdRef = useRef(false);
  const knownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const id = setTimeout(() => setCardDebounced(cardInput.trim()), CARD_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [cardInput]);

  useEffect(() => {
    const onVisibility = () => {
      setIsPollingEnabled(document.visibilityState !== 'hidden');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const queryArgs = useMemo(
    () => ({
      filter,
      page,
      pageSize: FEED_PAGE_SIZE,
      card: cardDebounced || undefined,
      window: FEED_WINDOW,
    }),
    [filter, page, cardDebounced],
  );

  const { data, isLoading, isFetching } = useGetFeedQuery(queryArgs, {
    pollingInterval: isPollingEnabled ? POLLING_INTERVAL_MS : 0,
    refetchOnMountOrArgChange: true,
  });

  const setFilter = useCallback((next: FeedFilter) => {
    setFilterState(next);
    setPage(1);
    setDisplayedItems([]);
    setPendingItems([]);
    knownIdsRef.current = new Set();
  }, []);

  const setCardSearch = useCallback((next: string) => {
    setCardInput(next);
    setPage(1);
    setDisplayedItems([]);
    setPendingItems([]);
    knownIdsRef.current = new Set();
  }, []);

  useEffect(() => {
    if (!data) return;

    const incoming: FeedItem[] = isWantResponse(data, filter)
      ? (data as FeedWantResponse).items
      : (data as FeedTradeResponse).items;

    if (page > 1) {
      setDisplayedItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        const appended = incoming.filter((i) => !seen.has(i.id));
        appended.forEach((i) => knownIdsRef.current.add(i.id));
        return [...prev, ...appended];
      });
      return;
    }

    if (knownIdsRef.current.size === 0) {
      setDisplayedItems(incoming);
      incoming.forEach((i) => knownIdsRef.current.add(i.id));
      return;
    }

    const brandNew = incoming.filter((i) => !knownIdsRef.current.has(i.id));
    if (brandNew.length === 0) return;
    brandNew.forEach((i) => knownIdsRef.current.add(i.id));

    const shouldBuffer = isPaused || scrolledBeyondThresholdRef.current;
    if (shouldBuffer) {
      setPendingItems((prev) => [...brandNew, ...prev]);
    } else {
      setDisplayedItems((prev) => [...brandNew, ...prev]);
    }
  }, [data, filter, isPaused, page]);

  const hasMore = data?.hasMore ?? false;

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    setPage((p) => p + 1);
  }, [isFetching, hasMore]);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  const flushPending = useCallback(() => {
    setDisplayedItems((prev) => [...pendingItems, ...prev]);
    setPendingItems([]);
  }, [pendingItems]);

  const onScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const target = e.currentTarget ?? (e.target as HTMLElement);
    scrolledBeyondThresholdRef.current =
      (target?.scrollTop ?? 0) > SCROLL_BUFFER_THRESHOLD_PX;
  }, []);

  return {
    filter,
    setFilter,
    cardSearch: cardInput,
    setCardSearch,
    items: displayedItems,
    isLoading,
    isFetching,
    hasMore,
    loadMore,
    isPaused,
    togglePause,
    pendingCount: pendingItems.length,
    flushPending,
    onScroll,
  };
};
